/**
 * Rejected Page
 *
 * Shown to users whose profile was rejected
 */

import { useAuth } from '../../hooks/useAuth';

export function Rejected() {
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  return (
    <div class="flex items-center justify-center py-12">
      <div class="max-w-2xl w-full">
          <div class="bg-white shadow-lg rounded-lg overflow-hidden">
            {/* Error header */}
            <div class="bg-red-50 px-6 py-12 text-center">
              <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-100 mb-4">
                <svg class="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h2 class="text-3xl font-bold text-gray-900 mb-2">
                Solicitud No Aprobada
              </h2>
              <p class="text-lg text-gray-600">
                Lamentamos informarte que tu solicitud no ha sido aprobada
              </p>
            </div>

            {/* Information section */}
            <div class="px-6 py-8">
              <div class="space-y-6">
                {/* Rejection reason */}
                {user.rejection_reason && (
                  <div class="bg-gray-50 rounded-lg p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-2">
                      Motivo de la decisión
                    </h3>
                    <p class="text-gray-700">
                      {user.rejection_reason}
                    </p>
                  </div>
                )}

                {/* Next steps */}
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 mb-3">
                    ¿Qué puedes hacer ahora?
                  </h3>
                  <div class="space-y-4">
                    <div class="flex">
                      <div class="flex-shrink-0">
                        <svg class="h-6 w-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div class="ml-4">
                        <h4 class="font-medium text-gray-900 mb-1">
                          Solicita más información
                        </h4>
                        <p class="text-gray-600 text-sm">
                          Contáctanos para obtener detalles adicionales sobre la decisión y posibles pasos a seguir.
                        </p>
                        <a
                          href="mailto:soporte@fitbook.es"
                          class="inline-block mt-2 text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          soporte@fitbook.es →
                        </a>
                      </div>
                    </div>

                    <div class="flex">
                      <div class="flex-shrink-0">
                        <svg class="h-6 w-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      </div>
                      <div class="ml-4">
                        <h4 class="font-medium text-gray-900 mb-1">
                          Presenta una apelación
                        </h4>
                        <p class="text-gray-600 text-sm">
                          Si crees que hubo un error o tienes documentación adicional, puedes solicitar una revisión de tu caso.
                        </p>
                      </div>
                    </div>

                    <div class="flex">
                      <div class="flex-shrink-0">
                        <svg class="h-6 w-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div class="ml-4">
                        <h4 class="font-medium text-gray-900 mb-1">
                          Actualiza tu certificación
                        </h4>
                        <p class="text-gray-600 text-sm">
                          Si el motivo está relacionado con tu certificación, asegúrate de obtener la documentación necesaria y vuelve a aplicar.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact information */}
                <div class="bg-blue-50 border-l-4 border-blue-400 p-4">
                  <div class="flex">
                    <div class="flex-shrink-0">
                      <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                      </svg>
                    </div>
                    <div class="ml-3">
                      <h4 class="text-sm font-semibold text-blue-800 mb-1">
                        Estamos aquí para ayudarte
                      </h4>
                      <p class="text-sm text-blue-700">
                        Nuestro equipo está disponible para responder tus preguntas y ayudarte a entender los próximos pasos.
                        Contáctanos en{' '}
                        <a href="mailto:soporte@fitbook.es" class="font-medium underline">
                          soporte@fitbook.es
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User info */}
          <div class="mt-6 bg-white shadow rounded-lg px-6 py-4">
            <div class="flex items-center justify-between">
              <div>
                <p class="text-sm text-gray-500">Usuario</p>
                <p class="text-lg font-medium text-gray-900">{user.email}</p>
              </div>
              <div class="text-right">
                <p class="text-sm text-gray-500">Estado actual</p>
                <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  Solicitud Rechazada
                </span>
              </div>
            </div>
          </div>
      </div>
    </div>
  );
}
